import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import api from '../../utils/apiClient';
import { 
  Box, 
  Typography, 
  List, 
  ListItemButton, 
  ListItemText, 
  Collapse, 
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoveIcon from '@mui/icons-material/DriveFileMove';
import Checkbox from '@mui/material/Checkbox';
import { useNotifications } from '../../context/NotificationContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Валидация и парсинг IP-диапазона
// Поддерживаемые форматы:
// - CIDR: 192.168.1.0/24
// - Start-End: 192.168.1.100-192.168.1.108
// - Одиночный IP: 192.168.1.100
const parseAndValidateIpRange = (input) => {
  if (!input || input.trim() === '') {
    return { valid: true, empty: true };
  }

  const trimmed = input.trim();

  // Проверка CIDR формата
  const cidrRegex = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
  if (cidrRegex.test(trimmed)) {
    const [ip, mask] = trimmed.split('/');
    const octets = ip.split('.');

    // Проверка октетов (0-255)
    for (const octet of octets) {
      const num = parseInt(octet, 10);
      if (num < 0 || num > 255) {
        return { valid: false, error: 'ipValidationOctets' };
      }
    }

    // Проверка маски (0-32)
    const maskNum = parseInt(mask, 10);
    if (maskNum < 0 || maskNum > 32) {
      return { valid: false, error: 'ipValidationMask' };
    }

    return { valid: true, format: 'cidr', cidr: trimmed };
  }

  // Проверка формата start-end (с дефисом)
  const rangeRegex = /^(\d{1,3}\.){3}\d{1,3}\s*-\s*(\d{1,3}\.){3}\d{1,3}$/;
  if (rangeRegex.test(trimmed)) {
    const [start, end] = trimmed.split('-').map(s => s.trim());

    // Валидация октетов для start
    const startOctets = start.split('.');
    for (const octet of startOctets) {
      const num = parseInt(octet, 10);
      if (num < 0 || num > 255) {
        return { valid: false, error: 'ipValidationStartOctets' };
      }
    }

    // Валидация октетов для end
    const endOctets = end.split('.');
    for (const octet of endOctets) {
      const num = parseInt(octet, 10);
      if (num < 0 || num > 255) {
        return { valid: false, error: 'ipValidationEndOctets' };
      }
    }

    // Проверка что start <= end
    const startParts = startOctets.map(Number);
    const endParts = endOctets.map(Number);

    for (let i = 0; i < 4; i++) {
      if (startParts[i] < endParts[i]) {
        break;
      }
      if (startParts[i] > endParts[i]) {
        return { valid: false, error: 'ipValidationStartEnd' };
      }
    }

    return { valid: true, format: 'range', start, end };
  }

  // Проверка одиночного IP (преобразуем в /32)
  const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipRegex.test(trimmed)) {
    const octets = trimmed.split('.');
    for (const octet of octets) {
      const num = parseInt(octet, 10);
      if (num < 0 || num > 255) {
        return { valid: false, error: 'ipValidationEndOctetsSingle' };
      }
    }

    // Преобразуем одиночный IP в CIDR /32
    return { valid: true, format: 'cidr', cidr: `${trimmed}/32` };
  }

  return {
    valid: false,
    error: 'ipValidationFormat'
  };
};

const LocationTree = ({
  tree,
  deviceByIdMap,
  onDeviceClick,
  onOwnerClick,
  getStatusStyle,
  onCreateLocation,
  onDeleteLocation,
  canCreate = true,
  canDelete = true,
  // Пропсы для выбора устройств
  selectedDeviceIds = [],
  onDeviceSelect,
  // Пропсы для обновления данных после изменений
  onDataUpdate,
  // Пропс для открытия диалога перемещения извне
  onOpenMoveDialog,
  // Пропсы для настроек колонок (как в таблице)
  visibleColumns = {},
  columnOrder = [],
  calculateDeviceUptime,
  getUptimeColor
}) => {
  const { showSuccess, showError, showWarning } = useNotifications();
  const { t } = useTranslation('dashboard');
  
  React.useEffect(() => {
  }, [tree]);
  
  const rootChildKeys = React.useMemo(() => Object.keys(tree?.children || {}), [tree]);
  const singleRootKey = rootChildKeys.length === 1 ? rootChildKeys[0] : null;

  const [expandedNodes, setExpandedNodes] = React.useState(() => (
    singleRootKey ? { [singleRootKey]: true } : {}
  ));

  // Состояние для диалогов
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedNode, setSelectedNode] = React.useState(null);
  const [newLocationName, setNewLocationName] = React.useState('');
  const [newLocationType, setNewLocationType] = React.useState('rack');
  const [newLocationDescription, setNewLocationDescription] = React.useState('');
  const [newLocationIpRanges, setNewLocationIpRanges] = React.useState(['']);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [deviceMenuAnchor, setDeviceMenuAnchor] = React.useState(null);
  const [selectedDevice, setSelectedDevice] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  // Debounce для обновления данных
  const updateTimeoutRef = React.useRef(null);
  const debouncedUpdate = React.useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      if (onDataUpdate) {
        onDataUpdate();
      }
    }, 500);
  }, [onDataUpdate]);

  // Cleanup таймера при размонтировании
  React.useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Функция для извлечения UUID локации из составного ключа
  // Ключи имеют формат: "root/hosting:uuid/loc:uuid" или "root/hosting:uuid"
  const extractLocationUUID = (nodeId) => {
    if (!nodeId || nodeId === 'root') return null;
    
    // Регулярное выражение для поиска UUID
    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    
    // Ищем последний UUID в строке (для вложенных локаций берем самую глубокую)
    const parts = nodeId.split('/');
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];
      // Проверяем части с префиксом "loc:" для локаций
      if (part.startsWith('loc:')) {
        const match = part.match(uuidRegex);
        if (match) return match[0];
      }
    }
    
    // Если не нашли loc:, ищем любой UUID (для hosting)
    const match = nodeId.match(uuidRegex);
    return match ? match[0] : null;
  };

  // Функции для работы с API

  // API: Создание новой локации
  const apiCreateLocation = async (name, type, parentId, description = '', ipRangesInput = []) => {
    try {
      setIsLoading(true);

      // Извлекаем UUID из составного ключа
      const actualParentId = extractLocationUUID(parentId);

      const payload = {
        name,
        type,
        description
      };

      // Добавляем parent_id только если он не root и является валидным UUID
      if (actualParentId) {
        payload.parent_id = actualParentId;
      }

      // Парсим и валидируем IP-диапазоны
      const parsedRanges = ipRangesInput
        .filter(range => range && range.trim() !== '')
        .map(range => {
          const parsed = parseAndValidateIpRange(range);
          if (!parsed.valid || parsed.empty) return null;

          if (parsed.format === 'cidr') {
            return { cidr: parsed.cidr };
          } else if (parsed.format === 'range') {
            return { start: parsed.start, end: parsed.end };
          }
          return null;
        })
        .filter(range => range !== null);

      if (parsedRanges.length > 0) {
        payload.ip_ranges = parsedRanges;
      }

      const response = await api.post(`${API_BASE_URL}locations`, payload);

      showSuccess(t('locationCreated', { name }));

      // Обновляем данные с debounce
      debouncedUpdate();
      if (!onDataUpdate && onCreateLocation) {
        onCreateLocation(parentId, name);
      }

      return response.data;
    } catch (error) {
      showError(t('locationCreateError', { message: error.response?.data?.message || error.message }));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API: Удаление локации
  const apiDeleteLocation = async (locationId) => {
    try {
      setIsLoading(true);

      // Извлекаем UUID из составного ключа
      const actualLocationId = extractLocationUUID(locationId);

      if (!actualLocationId) {
        showError(t('locationCannotDetermineId'));
        return;
      }

      await api.delete(`${API_BASE_URL}locations/${actualLocationId}`);

      showSuccess(t('locationDeleted'));

      // Обновляем данные с debounce
      debouncedUpdate();
      if (!onDataUpdate && onDeleteLocation) {
        onDeleteLocation(locationId);
      }
    } catch (error) {
      showError(t('locationDeleteError', { message: error.response?.data?.message || error.message }));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };


  // Функция для построения полного пути к узлу
  const buildNodePath = React.useCallback((targetNodeId, currentNode = tree, currentPath = []) => {
    if (!currentNode) return null;
    
    // Проверяем текущий узел
    if (currentNode.id === targetNodeId || currentNode.name === targetNodeId) {
      // Если это корневой узел, возвращаем только его имя
      if (currentNode.id === 'root' || currentNode.name === 'root') {
        return currentNode.name;
      }
      return [...currentPath, currentNode.name].join(' / ');
    }
    
    // Ищем в дочерних узлах
    if (currentNode.children) {
      for (const [key, childNode] of Object.entries(currentNode.children)) {
        const childPath = currentNode.id === 'root' || currentNode.name === 'root' 
          ? [] 
          : [...currentPath, currentNode.name];
        const result = buildNodePath(targetNodeId, childNode, childPath);
        if (result) return result;
      }
    }
    
    return null;
  }, [tree]);

  React.useEffect(() => {
    if (!singleRootKey) {
      return;
    }
    setExpandedNodes((prev) => (
      prev[singleRootKey] ? prev : { ...prev, [singleRootKey]: true }
    ));
  }, [singleRootKey]);

  const toggleNode = (nodeId, fallbackExpanded = false) => {
    setExpandedNodes((prev) => {
      const current = prev[nodeId];
      const resolvedCurrent = current === undefined ? fallbackExpanded : current;
      return {
        ...prev,
        [nodeId]: !resolvedCurrent,
      };
    });
  };

  // Обработчики для создания и удаления
  const handleCreateLocation = (parentNode) => {
    setSelectedNode(parentNode);
    setNewLocationName('');
    setCreateDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteLocation = (node) => {
    setSelectedNode(node);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event, node) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedNode(node);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNode(null);
  };

  const handleCreateConfirm = async () => {
    if (newLocationName.trim() && selectedNode) {
      // Валидация IP-диапазонов
      const validationResults = newLocationIpRanges
        .filter(range => range && range.trim() !== '')
        .map(range => parseAndValidateIpRange(range));

      const invalidRange = validationResults.find(result => !result.valid);
      if (invalidRange) {
        showError(t(invalidRange.error) || t('locationInvalidIp'));
        return;
      }

      try {
        await apiCreateLocation(
          newLocationName.trim(),
          newLocationType,
          selectedNode.id || selectedNode.name,
          newLocationDescription.trim(),
          newLocationIpRanges
        );
        setCreateDialogOpen(false);
        setNewLocationName('');
        setNewLocationType('rack');
        setNewLocationDescription('');
        setNewLocationIpRanges(['']);
        setSelectedNode(null);
      } catch (error) {
        // Ошибка уже обработана в apiCreateLocation
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedNode) {
      try {
        await apiDeleteLocation(selectedNode.id || selectedNode.name);
        setDeleteDialogOpen(false);
        setSelectedNode(null);
      } catch (error) {
        // Ошибка уже обработана в apiDeleteLocation
      }
    }
  };

  const handleCreateCancel = () => {
    setCreateDialogOpen(false);
    setNewLocationName('');
    setNewLocationType('rack');
    setNewLocationDescription('');
    setNewLocationIpRanges(['']);
    setSelectedNode(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedNode(null);
  };

  // Обработчики для перемещения устройств
  const handleMoveSelectedDevices = () => {
    if (selectedDeviceIds.length === 0) {
      showWarning(t('locationSelectDevices'));
      return;
    }
    if (onOpenMoveDialog) {
      onOpenMoveDialog();
    }
  };

  // Обработчики для IP-диапазонов
  const handleAddIpRange = () => {
    setNewLocationIpRanges([...newLocationIpRanges, '']);
  };

  const handleRemoveIpRange = (index) => {
    const updatedRanges = newLocationIpRanges.filter((_, i) => i !== index);
    setNewLocationIpRanges(updatedRanges.length > 0 ? updatedRanges : ['']);
  };

  const handleIpRangeChange = (index, value) => {
    const updatedRanges = [...newLocationIpRanges];
    updatedRanges[index] = value;
    setNewLocationIpRanges(updatedRanges);
  };


  // Функция для проверки, можно ли создать локацию под этим узлом
  // Можно создавать под локациями и под hosting узлами
  // Нельзя создавать только под root
  const canCreateLocationUnder = React.useCallback((node) => {
    if (!node || !node.id) return false;
    
    const nodeId = node.id || node.name;
    
    // Нельзя создавать под root
    if (nodeId === 'root') return false;
    
    // Можно создавать под hosting узлами (они начинаются с "root/hosting:" и не содержат "/loc:")
    // Формат hosting: "root/hosting:uuid"
    if (nodeId.startsWith('root/hosting:') && !nodeId.includes('/loc:')) {
      return true;
    }
    
    // Можно создавать под локациями (которые содержат "/loc:" в пути)
    return nodeId.includes('/loc:');
  }, []);

  // Функция для проверки, можно ли удалить локацию
  // Можно удалять локации (которые содержат "/loc:" в пути), там же где можно и создать
  // Нельзя удалять: root, hosting узлы
  const canDeleteLocation = React.useCallback((node) => {
    if (!node || !node.id) return false;
    
    const nodeId = node.id || node.name;
    
    // Нельзя удалять root
    if (nodeId === 'root') return false;
    
    // Нельзя удалять hosting узлы (они начинаются с "root/hosting:" и не содержат "/loc:")
    // Формат hosting: "root/hosting:uuid"
    if (nodeId.startsWith('root/hosting:') && !nodeId.includes('/loc:')) {
      return false;
    }
    
    // Можно удалять любую локацию (которая содержит "/loc:" в пути)
    // Такая же логика, как и для создания - если можно создать под ней, можно и удалить
    return nodeId.includes('/loc:');
  }, []);

  // Функция для получения всех ID устройств из узла и его дочерних узлов
  const getAllDeviceIdsFromNode = React.useCallback((node) => {
    const deviceIds = [];

    const collectDeviceIds = (currentNode) => {
      // Добавляем устройства текущего узла
      if (Array.isArray(currentNode.devices)) {
        currentNode.devices.forEach(device => {
          if (device.id) {
            deviceIds.push(device.id);
          }
        });
      }

      // Рекурсивно обходим дочерние узлы
      if (currentNode.children) {
        Object.values(currentNode.children).forEach(childNode => {
          collectDeviceIds(childNode);
        });
      }
    };

    collectDeviceIds(node);
    return deviceIds;
  }, []);

  // Функция для подсчета online/offline/special устройств в узле и его дочерних узлах
  const getDeviceStats = React.useCallback((node) => {
    let online = 0;
    let offline = 0;
    let special = 0; // repair, maintenance и другие специальные статусы
    let total = 0;

    const collectStats = (currentNode) => {
      // Считаем устройства текущего узла
      if (Array.isArray(currentNode.devices)) {
        currentNode.devices.forEach(device => {
          const deviceData = deviceByIdMap.get(device.id);
          if (deviceData) {
            const status = deviceData.status;
            const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : status;

            // Специальные статусы (repair, maintenance и т.д.) - всё кроме online/offline/null
            if (normalizedStatus && normalizedStatus !== 'online' && normalizedStatus !== 'offline') {
              special++;
              total++; // Учитываем в общем количестве
            } else if (normalizedStatus === 'online') {
              online++;
              total++;
            } else if (normalizedStatus === 'offline') {
              offline++;
              total++;
            } else {
              // null/undefined статусы
              total++;
            }
          }
        });
      }

      // Рекурсивно обходим дочерние узлы
      if (currentNode.children) {
        Object.values(currentNode.children).forEach(childNode => {
          collectStats(childNode);
        });
      }
    };

    collectStats(node);
    return { online, offline, special, total };
  }, [deviceByIdMap]);

  // Функция для определения состояния чекбокса узла
  const getNodeCheckboxState = React.useCallback((node) => {
    const allDeviceIds = getAllDeviceIdsFromNode(node);
    if (allDeviceIds.length === 0) {
      return { checked: false, indeterminate: false };
    }
    
    const selectedCount = allDeviceIds.filter(id => selectedDeviceIds.includes(id)).length;
    
    if (selectedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (selectedCount === allDeviceIds.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  }, [getAllDeviceIdsFromNode, selectedDeviceIds]);

  // Обработчик выбора всех устройств в узле
  const handleNodeSelect = React.useCallback((event, node) => {
    event.stopPropagation();
    
    if (!onDeviceSelect) return;
    
    const allDeviceIds = getAllDeviceIdsFromNode(node);
    const { checked } = getNodeCheckboxState(node);
    
    if (checked || getNodeCheckboxState(node).indeterminate) {
      // Снимаем выбор со всех устройств в узле
      allDeviceIds.forEach(deviceId => {
        if (selectedDeviceIds.includes(deviceId)) {
          onDeviceSelect(deviceId);
        }
      });
    } else {
      // Выбираем все устройства в узле
      allDeviceIds.forEach(deviceId => {
        if (!selectedDeviceIds.includes(deviceId)) {
          onDeviceSelect(deviceId);
        }
      });
    }
  }, [getAllDeviceIdsFromNode, getNodeCheckboxState, onDeviceSelect, selectedDeviceIds]);

  // Функция для сортировки IP-адресов
  const sortByIp = (a, b) => {
    const deviceA = deviceByIdMap.get(a.id);
    const deviceB = deviceByIdMap.get(b.id);
    const ipA = deviceA?.ip || '';
    const ipB = deviceB?.ip || '';

    // Преобразуем IP в числовое значение для корректной сортировки
    const ipToNumber = (ip) => {
      if (!ip) return 0;
      const parts = ip.split('.');
      if (parts.length !== 4) return 0;
      return parts.reduce((acc, octet) => acc * 256 + parseInt(octet, 10) || 0, 0);
    };

    return ipToNumber(ipA) - ipToNumber(ipB);
  };

  // Ширины колонок для табличного вида
  const columnWidths = {
    model: 100,
    modelName: 120,
    status: 80,
    algorithm: 80,
    mac: 140,
    ip: 120,
    serialNumber: 160,
    tags: 150,
    hashrate: 100,
    temperature: 100,
    fan: 120,
    worker: 150,
    client: 120,
    uptime: 70,
  };

  // Названия колонок для заголовка
  const columnLabels = {
    model: t('columnDevice'),
    modelName: t('columnModel'),
    status: t('columnStatus'),
    algorithm: t('columnAlgorithm'),
    mac: 'MAC',
    ip: 'IP',
    serialNumber: t('columnSerial'),
    tags: t('columnTags'),
    hashrate: 'Hashrate',
    temperature: t('columnTempShort'),
    fan: t('columnFan'),
    worker: 'Worker',
    client: t('columnClient'),
    uptime: 'Uptime',
  };

  // Функция для рендеринга отдельного поля устройства согласно настройкам
  const renderDeviceField = (column, device, id, width) => {
    const statusStyle = getStatusStyle(device.status);
    const statusText = device.status === 'online' ? 'Online' :
                      device.status === 'offline' ? 'Offline' :
                      device.status === null ? 'N/A' :
                      device.status.charAt(0).toUpperCase() + device.status.slice(1);

    const cellStyle = { width, minWidth: width, maxWidth: width, flexShrink: 0 };

    switch (column) {
      case 'model':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" sx={{ color: 'primary.main', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {device.model || '—'}
            </Typography>
          </Box>
        );
      case 'modelName':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {device.modelName || '—'}
            </Typography>
          </Box>
        );
      case 'status':
        return (
          <Box key={column} sx={cellStyle}>
            <Box component="span" sx={{
              padding: '2px 6px',
              borderRadius: '12px',
              fontSize: '12px',
              backgroundColor: statusStyle.backgroundColor,
              color: statusStyle.color,
            }}>
              {statusText}
            </Box>
          </Box>
        );
      case 'algorithm':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" color="textSecondary">
              {device.algorithm || '—'}
            </Typography>
          </Box>
        );
      case 'mac':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
              {device.mac || '—'}
            </Typography>
          </Box>
        );
      case 'ip':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
              {device.ip || '—'}
            </Typography>
          </Box>
        );
      case 'serialNumber':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {device.serialNumber || '—'}
            </Typography>
          </Box>
        );
      case 'tags':
        return (
          <Box key={column} sx={{ ...cellStyle, display: 'flex', gap: 0.5, overflow: 'hidden' }}>
            {Array.isArray(device.tags) && device.tags.length > 0 ? (
              device.tags.slice(0, 2).map((tag) => <Chip key={tag} label={tag} size="small" />)
            ) : (
              <Typography variant="body2" color="textSecondary">—</Typography>
            )}
            {device.tags?.length > 2 && (
              <Chip label={`+${device.tags.length - 2}`} size="small" variant="outlined" />
            )}
          </Box>
        );
      case 'hashrate':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" color="textSecondary">
              {device.hashrate || '—'}
            </Typography>
          </Box>
        );
      case 'temperature':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" color="textSecondary">
              {Array.isArray(device.temperature) && device.temperature.length > 0
                ? `${device.temperature.join('/')}°C`
                : '—'}
            </Typography>
          </Box>
        );
      case 'fan':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" color="textSecondary">
              {Array.isArray(device.fan) && device.fan.length > 0
                ? device.fan.join('/')
                : '—'}
            </Typography>
          </Box>
        );
      case 'worker':
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {device.worker && device.worker !== 'N/A' ? device.worker : '—'}
            </Typography>
          </Box>
        );
      case 'client':
        return (
          <Box key={column} sx={cellStyle}>
            {device.owner ? (
              <Typography
                variant="body2"
                sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                onClick={(event) => {
                  event.stopPropagation();
                  onOwnerClick(device.owner.id);
                }}
              >
                {device.client}
              </Typography>
            ) : (
              <Typography variant="body2" color="textSecondary">—</Typography>
            )}
          </Box>
        );
      case 'uptime':
        if (!calculateDeviceUptime) {
          return (
            <Box key={column} sx={cellStyle}>
              <Typography variant="body2" color="textSecondary">—</Typography>
            </Box>
          );
        }
        const uptimeValue = calculateDeviceUptime(id);
        const uptimeColor = getUptimeColor ? getUptimeColor(uptimeValue) : '#4caf50';
        return (
          <Box key={column} sx={cellStyle}>
            <Typography variant="body2" sx={{ color: uptimeColor }}>
              {uptimeValue.toFixed(1)}%
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderDevices = (devices) => {
    if (!Array.isArray(devices) || devices.length === 0) {
      return null;
    }

    // Сортируем устройства по IP-адресу
    const sortedDevices = [...devices].sort(sortByIp);

    // Определяем порядок колонок (используем columnOrder если есть, иначе дефолтный)
    const defaultOrder = ['model', 'modelName', 'status', 'algorithm', 'mac', 'ip', 'serialNumber', 'tags', 'hashrate', 'temperature', 'fan', 'worker', 'uptime', 'client'];
    const order = columnOrder.length > 0 ? columnOrder : defaultOrder;

    // Фильтруем только видимые колонки
    const visibleOrder = order.filter(col => col !== 'hashrateGraph' && visibleColumns[col] !== false);

    // Считаем общую ширину таблицы
    const totalWidth = visibleOrder.reduce((sum, col) => sum + (columnWidths[col] || 100), 0) + 50; // +50 для чекбокса

    return (
      <Box sx={{
        overflowX: 'auto',
        ml: 4,
        '&::-webkit-scrollbar': { height: '8px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' }
      }}>
        {/* Заголовок таблицы */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minWidth: totalWidth,
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1
        }}>
          <Box sx={{ width: 42, minWidth: 42, flexShrink: 0 }} /> {/* Место для чекбокса */}
          {visibleOrder.map((column) => (
            <Box key={column} sx={{
              width: columnWidths[column] || 100,
              minWidth: columnWidths[column] || 100,
              flexShrink: 0,
              px: 0.5
            }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                {columnLabels[column] || column}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Строки устройств */}
        {sortedDevices.map(({ id }) => {
          const device = deviceByIdMap.get(id) || {};
          const isSelected = selectedDeviceIds.includes(id);

          return (
            <Box
              key={id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                py: 0.5,
                minWidth: totalWidth,
                borderRadius: 1,
                backgroundColor: isSelected ? 'action.selected' : 'transparent',
                '&:hover': { backgroundColor: isSelected ? 'action.selected' : 'action.hover' },
                cursor: 'pointer'
              }}
              onClick={() => onDeviceClick(id)}
            >
              {/* Чекбокс */}
              <Box sx={{ width: 42, minWidth: 42, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => onDeviceSelect && onDeviceSelect(id)}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>

              {/* Поля устройства */}
              {visibleOrder.map((column) => (
                <Box key={column} sx={{ px: 0.5 }}>
                  {renderDeviceField(column, device, id, columnWidths[column] || 100)}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderNode = (node, nodeKey, depth = 0, isTopLevel = false) => {
    const childrenKeys = Object.keys(node.children || {});
    const devices = node.devices || [];
    const defaultExpanded = isTopLevel && singleRootKey === nodeKey;
    const isExpanded = expandedNodes[nodeKey] ?? defaultExpanded;

    // Получаем статистику устройств
    const deviceStats = getDeviceStats(node);
    const { online, offline, special, total } = deviceStats;

    // Считаем устройства для отображения (online + offline, без special)
    const regularDevices = online + offline;

    // Определяем цвет статуса (special статусы не влияют на цвет)
    const statusColor = offline > 0
      ? getStatusStyle('offline').backgroundColor  // Красный если есть offline
      : online > 0
        ? getStatusStyle('online').backgroundColor  // Зеленый если все online
        : undefined;  // Нет цвета если нет устройств или только special

    // Получаем состояние чекбокса для узла
    const checkboxState = getNodeCheckboxState(node);

    // Считаем количество выбранных устройств в этом узле
    const allDeviceIds = getAllDeviceIdsFromNode(node);
    const selectedInNode = allDeviceIds.filter(id => selectedDeviceIds.includes(id)).length;

    const nodeHeader = (
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Чекбокс для массового выбора устройств в узле */}
        {onDeviceSelect && total > 0 && (
          <Checkbox
            checked={checkboxState.checked}
            indeterminate={checkboxState.indeterminate}
            onChange={(e) => handleNodeSelect(e, node)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ ml: 1 }}
          />
        )}
        
        <ListItemButton
          onClick={() => toggleNode(nodeKey, defaultExpanded)}
          sx={{
            flex: 1,
            borderRadius: '4px',
            mb: 0.5,
            backgroundColor: statusColor ? `${statusColor}30` : total === 0 ? 'action.hover' : 'transparent',
            border: statusColor ? `1px solid ${statusColor}` : total === 0 ? '1px dashed' : '1px solid transparent',
            borderColor: total === 0 ? 'text.disabled' : undefined,
            pr: 5, // Оставляем место для кнопки управления
            opacity: total === 0 ? 0.7 : 1,
          }}
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                {/* Левая часть: Название и статистика online/offline */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1">{node.name}</Typography>
                  {total > 0 && (
                    <Typography variant="subtitle1" color="textSecondary">
                      {online}/{regularDevices}
                      {special > 0 && ` (${special})`}
                    </Typography>
                  )}
                  {selectedInNode > 0 && (
                    <Chip
                      label={t('locationSelected', { count: selectedInNode })}
                      size="small"
                      color="primary"
                      sx={{ height: '20px', fontSize: '0.7rem' }}
                    />
                  )}
                </Box>

                {/* Правая часть: IP-диапазоны и количество устройств */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Отображение IP-диапазонов */}
                  {node.ip_ranges && Array.isArray(node.ip_ranges) && node.ip_ranges.length > 0 && (
                    <Tooltip
                      title={
                        <Box>
                          {node.ip_ranges.map((range, idx) => (
                            <Box key={idx} sx={{ mb: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                {range.cidr ? range.cidr : `${range.start} - ${range.end}`}
                              </Typography>
                              {range.description && (
                                <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                                  {range.description}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      }
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ cursor: 'help' }}
                      >
                        {node.ip_ranges.map(range =>
                          range.cidr ? range.cidr : `${range.start}-${range.end}`
                        ).join(', ')}
                      </Typography>
                    </Tooltip>
                  )}

                  {/* Количество устройств */}
                  <Typography
                    variant="caption"
                    color={total === 0 ? 'text.disabled' : 'textSecondary'}
                  >
                    {total} {total === 0 ? t('devicesCount_zero') : t('devicesCount', { count: total })}
                  </Typography>
                </Box>
              </Box>
            }
          />
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        
        {/* Кнопка управления - показываем для локаций и hosting (где можно создать или удалить), но не для "Без локации" */}
        {(canCreateLocationUnder(node) || canDeleteLocation(node)) && 
         !(node.id && node.id.includes('/no-location')) ? (
          <Box sx={{ 
            position: 'absolute', 
            right: 8, 
            top: '50%', 
            transform: 'translateY(-50%)'
          }}>
            <Tooltip title={t('locationManage')}>
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, node)}
                sx={{ 
                  backgroundColor: 'background.paper',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </Box>
    );

    return (
      <Box key={nodeKey} sx={{ ml: depth === 0 ? 0 : 3, mt: depth === 0 ? 0 : 1.5 }}>
        {depth === 0 ? nodeHeader : nodeHeader}
        <Collapse in={isExpanded} timeout={300} unmountOnExit>
          {renderDevices(devices)}
          {childrenKeys.map((childKey) => {
            const childNode = node.children[childKey];
            return renderNode(childNode, childNode.id || childKey, depth + 1);
          })}
        </Collapse>
      </Box>
    );
  };

  // Получаем состояние чекбокса для всего дерева (корня)
  const rootCheckboxState = React.useMemo(() => {
    if (!tree) return { checked: false, indeterminate: false };
    return getNodeCheckboxState(tree);
  }, [tree, getNodeCheckboxState]);

  // Обработчик выбора всех устройств в дереве
  const handleSelectAllDevices = React.useCallback((event) => {
    event.stopPropagation();
    if (!tree || !onDeviceSelect) return;
    handleNodeSelect(event, tree);
  }, [tree, onDeviceSelect, handleNodeSelect]);

  return (
    <Box sx={{ backgroundColor: 'background.paper', borderRadius: '8px', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Чекбокс для выбора всех устройств */}
          {onDeviceSelect && tree && (tree.totalDevices || 0) > 0 && (
            <Checkbox
              checked={rootCheckboxState.checked}
              indeterminate={rootCheckboxState.indeterminate}
              onChange={handleSelectAllDevices}
              size="small"
            />
          )}
          <Typography variant="h6">
            {t('locationTreeTitle')}
          </Typography>
          {selectedDeviceIds.length > 0 && (
            <Chip 
              label={t('locationSelected', { count: selectedDeviceIds.length })}
              size="small" 
              color="primary"
            />
          )}
        </Box>
        
        {/* Кнопка перемещения выбранных устройств */}
        {selectedDeviceIds.length > 0 && (
          <Tooltip title={t('locationMoveDevices')}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MoveIcon />}
              onClick={handleMoveSelectedDevices}
              disabled={isLoading}
            >
              {t('locationMove', { count: selectedDeviceIds.length })}
            </Button>
          </Tooltip>
        )}
      </Box>
      <List>
        {renderDevices(tree?.devices)}
        {rootChildKeys.map((childKey) => {
          const childNode = tree.children[childKey];
          const key = childNode.id || childKey;
          return renderNode(childNode, key, 0, true);
        })}
      </List>

      {/* Меню действий */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {canCreate && selectedNode && canCreateLocationUnder(selectedNode) && (
          <MenuItem onClick={() => handleCreateLocation(selectedNode)}>
            <AddIcon sx={{ mr: 1 }} />
            {t('locationAddSubdivision')}
          </MenuItem>
        )}
        {canDelete && selectedNode && canDeleteLocation(selectedNode) && (
          <MenuItem 
            onClick={() => handleDeleteLocation(selectedNode)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} />
            {t('common:delete')}
          </MenuItem>
        )}
      </Menu>

      {/* Диалог создания */}
      <Dialog open={createDialogOpen} onClose={handleCreateCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{t('locationCreateTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('locationNameLabel')}
            fullWidth
            variant="outlined"
            value={newLocationName}
            onChange={(e) => setNewLocationName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>{t('locationTypeLabel')}</InputLabel>
            <Select
              value={newLocationType}
              onChange={(e) => setNewLocationType(e.target.value)}
              label={t('locationTypeLabel')}
            >
              <MenuItem value="datacenter">{t('locationTypeDatacenter')}</MenuItem>
              <MenuItem value="room">{t('locationTypeRoom')}</MenuItem>
              <MenuItem value="rack">{t('locationTypeRack')}</MenuItem>
              <MenuItem value="shelf">{t('locationTypeShelf')}</MenuItem>
              <MenuItem value="other">{t('locationTypeOther')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label={t('locationDescLabel')}
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={newLocationDescription}
            onChange={(e) => setNewLocationDescription(e.target.value)}
            sx={{ mb: 3 }}
          />

          {/* IP-диапазоны */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
              {t('locationIpRanges')}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('locationIpFormats')}
            </Typography>
            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography variant="body2" component="div">
                • {t('ipFormatSingle')} <code>192.168.1.100</code>
              </Typography>
              <Typography variant="body2" component="div">
                • {t('ipFormatRange')} <code>192.168.1.100-192.168.1.108</code>
              </Typography>
              <Typography variant="body2" component="div">
                • {t('ipFormatCidr')} <code>192.168.1.0/24</code>
              </Typography>
            </Box>

            {newLocationIpRanges.map((range, index) => {
              const validation = parseAndValidateIpRange(range);
              const hasError = range && range.trim() !== '' && !validation.valid;

              return (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
                  <TextField
                    label={t('locationIpLabel', { index: index + 1 })}
                    variant="outlined"
                    size="small"
                    value={range}
                    onChange={(e) => handleIpRangeChange(index, e.target.value)}
                    placeholder="192.168.1.0/24"
                    fullWidth
                    disabled={isLoading}
                    error={hasError}
                    helperText={hasError ? t(validation.error) : ''}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveIpRange(index)}
                    disabled={isLoading || newLocationIpRanges.length === 1}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddIpRange}
              disabled={isLoading}
              sx={{ mt: 1 }}
            >
              {t('locationAddRange')}
            </Button>

            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
              {t('locationIpAutoAssign')}
            </Typography>
          </Box>

          {selectedNode && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              {t('locationParent', { path: buildNodePath(selectedNode.id || selectedNode.name) })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateCancel} disabled={isLoading}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleCreateConfirm}
            variant="contained"
            disabled={!newLocationName.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? t('locationCreating') : t('common:create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог удаления */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('locationDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('locationDeleteConfirm', { name: selectedNode?.name })}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {t('locationDeleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isLoading}>
            {t('common:cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            color="error"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? t('locationDeleting') : t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

LocationTree.propTypes = {
  tree: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    children: PropTypes.object,
    devices: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
    })),
    totalDevices: PropTypes.number,
  }).isRequired,
  deviceByIdMap: PropTypes.instanceOf(Map).isRequired,
  onDeviceClick: PropTypes.func.isRequired,
  onOwnerClick: PropTypes.func.isRequired,
  getStatusStyle: PropTypes.func.isRequired,
  onCreateLocation: PropTypes.func,
  onDeleteLocation: PropTypes.func,
  canCreate: PropTypes.bool,
  canDelete: PropTypes.bool,
  // PropTypes для выбора устройств
  selectedDeviceIds: PropTypes.array,
  onDeviceSelect: PropTypes.func,
  // PropTypes для обновления данных
  onDataUpdate: PropTypes.func,
  // PropTypes для открытия диалога перемещения
  onOpenMoveDialog: PropTypes.func,
  // PropTypes для настроек колонок
  visibleColumns: PropTypes.object,
  columnOrder: PropTypes.array,
  calculateDeviceUptime: PropTypes.func,
  getUptimeColor: PropTypes.func,
};

export default LocationTree;

